import { Box, Collapse, Fade, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { WarningBanner } from '@/components/notifications/WarningBanner/WarningBanner';
import {
  GraphQLSubscriptionErrors,
  isNotFoundError,
  isPureGraphQLError,
} from '@/graphql/errors';
import { deletedHouseholdIdsVar } from '@/graphql/reactiveVars';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';

import { useHouseholdValidationStatusSubscription } from '../graphql/HouseholdValidationStatus.generated';
import { useMarkValidationQuestionNotificationsRead } from '../hooks/useMarkValidationQuestionNotificationsRead';
import { getValidationQuestionsPath } from '../validationQuestions.paths';

/** Keep status updates active when the overview shows its own setup prompt. */
export function HouseholdValidationQuestionsBanner({
  householdId,
  showBanner = true,
}: {
  householdId: string;
  showBanner?: boolean;
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const transitionDuration = reducedMotion
    ? 0
    : theme.transitions.duration.short;
  const canEdit = useCanEditHouseholdData();
  const markNotificationsRead =
    useMarkValidationQuestionNotificationsRead(householdId);
  const trackUserEvent = useTrackUserEvent();
  const { reportError } = useReportError();
  // Apollo also updates normalized list and switcher entries on each emission.
  const { data, restart } = useHouseholdValidationStatusSubscription({
    variables: { householdId },
    ignoreResults: false,
    context: { graphQLErrorReporting: 'caller' },
    onError: (error) => {
      if (!isPureGraphQLError(error)) return;

      // Expiry ends the operation, not the socket. Restart only when every
      // error is the expected signal so mixed failures still get reported.
      if (
        error.graphQLErrors.every(
          ({ message }) =>
            message === GraphQLSubscriptionErrors.RECONNECT_MESSAGE
        )
      ) {
        restart();
        return;
      }

      const missingHousehold =
        isNotFoundError(error) &&
        error.graphQLErrors.every(
          ({ path }) => path?.length === 1 && path[0] === 'household'
        );
      if (missingHousehold) {
        const deletedIds = deletedHouseholdIdsVar();
        if (!deletedIds.includes(householdId)) {
          // The household route observes this state and renders its not-found view.
          deletedHouseholdIdsVar([...deletedIds, householdId]);
        }
        return;
      }
      reportError('Failed to load household validation status', error, {
        operationName: 'HouseholdValidationStatus',
      });
    },
  });
  const isCurrentHousehold = data?.household.id === householdId;
  const visible =
    showBanner &&
    isCurrentHousehold &&
    !!data?.household.hasPendingValidationQuestions;

  return (
    <Collapse
      in={visible}
      appear
      mountOnEnter
      unmountOnExit
      timeout={transitionDuration}
      easing={theme.transitions.easing.easeOut}
      sx={{ flexShrink: 0 }}
    >
      <Fade in={visible} appear timeout={transitionDuration}>
        <Box>
          {isCurrentHousehold && (
            <WarningBanner
              title="Processing is paused for this household."
              description="Complete validation questions to finish building the household and create deliverables."
              action={{
                label: 'View validation questions',
                disabled: !canEdit,
                onClick: () => {
                  markNotificationsRead();
                  navigate(getValidationQuestionsPath(householdId));
                  trackUserEvent(
                    'household_chat validation questions banner clicked',
                    {
                      householdId,
                    }
                  );
                },
              }}
            />
          )}
        </Box>
      </Fade>
    </Collapse>
  );
}
